/**
 * PIN vault — IndexedDB 永続化レイヤ (Option #2)。
 *
 * 設計判断:
 *   - **媒体**: IndexedDB (ookey-browser の `chrome.storage.local` 相当)。
 *     CLAUDE.md §2 ルール 6 注記の「Phase 4 で 暗号化済み保管庫アイテムの端末
 *     キャッシュとして IndexedDB の利用を検討」に該当する用途 (SK の wrap 形)。
 *     localStorage は同期 API のため非対称暗号 / 大きい payload に不向き。
 *   - **DB 名**: `ookey-secure` (preferences cache の `ookey` とは別 DB)。
 *     preferences は非機密データ、こちらは encrypted SK のため、誤って混在
 *     させないよう物理的に分離する。同じ DB に同居させると schema migration が
 *     片方の都合で発生したとき、もう片方の transaction まで巻き込む可能性。
 *   - **store**: `pinVault` (1 件のみ、key='current')。ユーザー単位ではなく
 *     現在のブラウザプロファイル単位で 1 つ (multi-account 切替時は logout で clear)。
 *   - **非対応環境**: read=null / write=no-op で動作継続 (テスト + SSR fallback)。
 */

import type { StoredPinVault } from '@/crypto/pin-vault'

const DB_NAME = 'ookey-secure'
const DB_VERSION = 1
const STORE_NAME = 'pinVault'
const KEY = 'current'

let cachedDb: IDBDatabase | null = null

/** 全 transaction wrapper の共通 reject 経路 (review I-4: tx.onabort 未処理で
 * Promise が永久 hang する問題の対策)。oncomplete / onerror / onabort のうち
 * 必ず 1 つは発火するため、3 つすべてを設定して executor を確実に決着させる。 */
const settleTx = (
  tx: IDBTransaction,
  resolve: () => void,
  reject: (err: unknown) => void,
): void => {
  tx.oncomplete = (): void => resolve()
  tx.onerror = (): void => reject(tx.error ?? new Error('IDB tx error'))
  tx.onabort = (): void => reject(tx.error ?? new Error('IDB tx aborted'))
}

const openDb = (): Promise<IDBDatabase | null> => {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null)
  if (cachedDb !== null) return Promise.resolve(cachedDb)

  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)

    req.onupgradeneeded = (): void => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME)
      }
    }
    req.onsuccess = (): void => {
      const db = req.result

      // review I-6: 別タブが DB_VERSION を上げて versionchange を発火した時、
      // browser が idle connection を close した時、cachedDb は使用不能な
      // closed handle になる。次回 transaction 呼出で InvalidStateError を
      // 投げて catch 側が wrong-PIN に誤分類するため、明示的に cache を破棄して
      // openDb() で再 open される導線を作る。
      db.onversionchange = (): void => {
        db.close()
        if (cachedDb === db) cachedDb = null
      }
      db.onclose = (): void => {
        if (cachedDb === db) cachedDb = null
      }
      cachedDb = db
      resolve(db)
    }
    req.onerror = (): void => reject(req.error)
    req.onblocked = (): void => reject(new Error('IDB open blocked'))
  })
}

/** 暗号化済 SK + メタを保存。失敗回数も含めて 1 transaction で上書き。 */
export const writePinVault = async (vault: StoredPinVault): Promise<void> => {
  const db = await openDb()
  if (!db) return

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')

    tx.objectStore(STORE_NAME).put(vault, KEY)
    settleTx(tx, resolve, reject)
  })
}

/** 取得失敗 / 未設定 / 非対応環境は `null`。 */
export const readPinVault = async (): Promise<StoredPinVault | null> => {
  const db = await openDb()
  if (!db) return null

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly')
    const req = tx.objectStore(STORE_NAME).get(KEY)
    let value: StoredPinVault | null = null

    req.onsuccess = (): void => {
      const result = req.result as StoredPinVault | undefined

      value = result ?? null
    }
    // tx 完了後に value を resolve することで onabort 経路でも reject される
    settleTx(tx, () => resolve(value), reject)
  })
}

/** ログアウト / PIN remove / lockout 時に vault を物理削除する。 */
export const clearPinVault = async (): Promise<void> => {
  const db = await openDb()
  if (!db) return

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')

    tx.objectStore(STORE_NAME).delete(KEY)
    settleTx(tx, resolve, reject)
  })
}

/** spec 用: cached DB 接続をリセット (テスト間隔離)。 */
export const __resetPinVaultDbForTest = (): void => {
  cachedDb?.close()
  cachedDb = null
}
